import React, { useEffect, useState } from "react"
import ReactDOM from "react-dom"
import { XMarkIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline"

// Lightweight media lightbox for share files. Theme-agnostic (dark scrim +
// white chrome) so it reads the same on the public landing page and the owner
// dashboard. Portals to <body> so it escapes any drawer/modal stacking.
//
// Props:
//   open      — render when true
//   kind      — "image" | "video"
//   url       — signed media URL (null while loading)
//   fileName  — shown in the header
//   loading   — fetching the signed URL
//   error     — truthy → show an error message
//   onClose   — close handler
//   onDownload — optional; renders a download button in the header
export default function FilePreview({ open, kind, url, fileName, loading, error, onClose, onDownload }) {
  // Fade media in once it loads. Image also gets a subtle zoom. Reset per url.
  const [imgLoaded, setImgLoaded] = useState(false)
  const [frameLoaded, setFrameLoaded] = useState(false)
  useEffect(() => { setImgLoaded(false); setFrameLoaded(false) }, [url])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === "Escape") onClose?.() }
    document.addEventListener("keydown", onKey)
    // Lock background scroll while the overlay is up.
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  const content = (
    <div
      className="fixed inset-0 z-[2147483000] bg-black/90 flex flex-col"
      onClick={onClose}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 text-white/90"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm truncate flex-1 min-w-0">{fileName}</span>
        {onDownload && (
          <button
            type="button"
            onClick={onDownload}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Download"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close preview"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Body */}
      <div
        className="flex-1 min-h-0 flex items-center justify-center p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {loading && (
          <div className="h-10 w-10 rounded-full border-2 border-white/30 border-t-white animate-spin" />
        )}
        {!loading && error && (
          <p className="text-white/70 text-sm">Preview unavailable.</p>
        )}
        {!loading && !error && url && kind === "image" && (
          <img
            src={url}
            alt={fileName}
            onLoad={() => setImgLoaded(true)}
            className={`max-w-full max-h-full object-contain select-none rounded-lg transition-all duration-300 ease-out ${imgLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
            draggable="false"
          />
        )}
        {!loading && !error && url && kind === "video" && (
          <video
            src={url}
            controls
            autoPlay
            className="max-w-full max-h-full rounded-lg shadow-lg"
          >
            Your browser does not support the video tag.
          </video>
        )}
        {!loading && !error && url && kind === "pdf" && (
          <iframe
            src={url}
            title={fileName}
            onLoad={() => setFrameLoaded(true)}
            className={`w-full h-full rounded-lg bg-white transition-opacity duration-300 ease-out ${frameLoaded ? "opacity-100" : "opacity-0"}`}
          />
        )}
      </div>
    </div>
  )

  return ReactDOM.createPortal(content, document.body)
}
